import {
    ANALYTICS_EVENTS,
    ANALYTICS_DISCOVERY_STEPS,
} from "./analytics.constants";

const MEASUREMENT_ID = process.env.REACT_APP_ECHO_VIBE_MEASUREMENT_ID;
const IS_DEVELOPMENT_SITE = process.env.REACT_APP_SITE_ENV === "development";
const SESSION_STARTED_KEY = "ev_session_started";

let initialized = false;

function isAnalyticsDisabled() {
    return IS_DEVELOPMENT_SITE;
}

function isAnalyticsEnabled() {
    if (isAnalyticsDisabled()) return false;
    return Boolean(MEASUREMENT_ID && typeof window !== "undefined" && window.gtag);
}

function sendEvent(eventName, params = {}) {
    if (!isAnalyticsEnabled()) return;
    window.gtag("event", eventName, params);
}

export function initAnalytics() {
    if (isAnalyticsDisabled()) return;
    if (initialized || !MEASUREMENT_ID || typeof window === "undefined") return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", MEASUREMENT_ID);
    window.gtag("set", "user_properties", { is_authenticated: "false" });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);

    initialized = true;
    trackSessionStarted();
}

function trackSessionStarted() {
    if (!isAnalyticsEnabled()) return;
    try {
        if (sessionStorage.getItem(SESSION_STARTED_KEY)) return;
        sessionStorage.setItem(SESSION_STARTED_KEY, "1");
    } catch {
        // sessionStorage unavailable — still fire once per page load
    }
    sendEvent(ANALYTICS_EVENTS.SESSION_STARTED);
}

export function track(eventName, params = {}) {
    sendEvent(eventName, params);
}

export function clearUser() {
    if (!isAnalyticsEnabled()) return;
    window.gtag("config", MEASUREMENT_ID, { user_id: undefined });
    window.gtag("set", "user_properties", { is_authenticated: "false" });
}

export function setUser(userId) {
    if (!isAnalyticsEnabled()) return;
    if (!userId) {
        clearUser();
        return;
    }
    window.gtag("config", MEASUREMENT_ID, { user_id: userId });
    window.gtag("set", "user_properties", { is_authenticated: "true" });
}

export function trackSignUp(method, extraParams = {}) {
    sendEvent(ANALYTICS_EVENTS.SIGN_UP, {
        method,
        is_authenticated: false,
        ...extraParams,
    });
}

export function trackLogin(method, extraParams = {}) {
    sendEvent(ANALYTICS_EVENTS.LOGIN, {
        method,
        is_authenticated: true,
        ...extraParams,
    });
}

/**
 * Call when a session is established (email login, activation, Google).
 * Sets GA4 user_id + is_authenticated user property and fires login or sign_up.
 */
export function trackUserAuthenticated({ userId, method, isNewUser = false, extraParams = {} }) {
    if (userId) setUser(userId);

    const params = { method, is_authenticated: true, ...extraParams };

    if (isNewUser) {
        sendEvent(ANALYTICS_EVENTS.SIGN_UP, params);
    } else {
        sendEvent(ANALYTICS_EVENTS.LOGIN, params);
    }
}

export function trackLogout() {
    sendEvent(ANALYTICS_EVENTS.LOGOUT, { is_authenticated: false });
    clearUser();
}

export function setPage(pageName) {
    if (!isAnalyticsEnabled() || !pageName) return;
    window.gtag("config", MEASUREMENT_ID, { page_title: pageName });
}

export function trackFeatureStarted(feature, extraParams = {}) {
    sendEvent(ANALYTICS_EVENTS.FEATURE_STARTED, { feature, ...extraParams });
}

export function trackScrolled(section) {
    sendEvent(ANALYTICS_EVENTS.SCROLLED, { section });
}

export function trackCtaClicked(cta, extraParams = {}) {
    sendEvent(ANALYTICS_EVENTS.CTA_CLICKED, { cta, ...extraParams });
}

export function trackItemSelected(itemType, itemId, position, extraParams = {}) {
    const params = { item_type: itemType, ...extraParams };

    if (itemId != null) {
        params.item_id = itemId;
    }
    if (position != null) {
        params.position = position;
    }

    sendEvent(ANALYTICS_EVENTS.ITEM_SELECTED, params);
}

/**
 * Post generation outcome (guest or logged-in).
 * @param {{ status: "success"|"failed", is_guest: boolean, generation_mode: string, post_count?: number, campaign_id?: string|null, error_code?: string|number|null }} params
 */
export function trackPostGenerated(params = {}) {
    const {
        status,
        is_guest: isGuest,
        generation_mode: generationMode,
        post_count: postCount,
        campaign_id: campaignId,
        error_code: errorCode,
        ...extra
    } = params;

    sendEvent(ANALYTICS_EVENTS.POST_GENERATED, {
        status,
        is_guest: Boolean(isGuest),
        generation_mode: generationMode ?? "",
        post_count: postCount ?? 0,
        campaign_id: campaignId ?? "",
        ...(errorCode != null && errorCode !== "" ? { error_code: String(errorCode) } : {}),
        ...extra,
    });
}

export function trackProfileCreated({ industry, category } = {}) {
    sendEvent(ANALYTICS_DISCOVERY_STEPS.PROFILE_CREATED, {
        industry: industry ?? "",
        category: category ?? "",
    });
}
